import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError(t('forgotPassword.emailRequired'));
      return;
    }

    if (!validateEmail(email?.trim())) {
      setError(t('forgotPassword.emailInvalid'));
      return;
    }

    try {
      setLoading(true);
      const result = await authService?.resetPassword(email?.trim()?.toLowerCase());

      if (result?.error) {
        setError(result?.error?.message || t('forgotPassword.errorMessage'));
        return;
      }

      setEmailSent(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(t('forgotPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />

      <div className="pt-4 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-8">
            {emailSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="MailCheck" size={32} className="text-success" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{t('forgotPassword.title')}</h1>
                <p className="text-muted-foreground mb-6">
                  {t('forgotPassword.successMessage')}
                </p>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setEmailSent(false);
                      setEmail('');
                    }}
                  >
                    {t('forgotPassword.submitButton')}
                  </Button>
                  <Link
                    to="/user-sign-in"
                    className="block text-center text-sm text-primary hover:underline"
                  >
                    {t('forgotPassword.backToSignIn')}
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Icon name="KeyRound" size={32} className="text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">{t('forgotPassword.title')}</h1>
                  <p className="text-muted-foreground">
                    {t('forgotPassword.subtitle')}
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-error/10 border border-error rounded-lg flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                    <span className="text-sm text-error">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('forgotPassword.emailLabel')}
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e?.target?.value);
                        if (error) setError('');
                      }}
                      placeholder={t('forgotPassword.emailPlaceholder')}
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    loading={loading}
                  >
                    {t('forgotPassword.submitButton')}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/user-sign-in"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Icon name="ArrowLeft" size={14} />
                    {t('forgotPassword.backToSignIn')}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
